import { Link } from "react-router-dom";
import { Check, ArrowRight, MessageCircle, Phone, ArrowUpRight, Info } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { useSEO } from "@/components/site/SEO";
import { SITE, whatsappLink, telLink } from "@/config/site";
import { track } from "@/lib/analytics";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingCTA } from "@/components/site/FloatingCTA";
import { Marquee } from "@/components/site/Marquee";
import { QuoteForm } from "@/sections/QuoteForm";
import { Reveal } from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getService, servicePricing, SERVICE_PAGES, DEFAULT_SERVICE_FAQS } from "@/data/services";
import { IMAGES } from "@/data/content";

const IMG_FOR = {
  residential: IMAGES.emptyRoom,
  commercial: IMAGES.kitchen,
};

export default function ServicePage({ slug }) {
  useLenis();
  const page = getService(slug);
  const canonical = `${SITE.url}/${slug}`;
  useSEO({
    title: page?.metaTitle,
    description: page?.metaDescription,
    canonical,
    jsonLd: page && {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: page.name,
      name: page.h1,
      description: page.metaDescription,
      areaServed: ["Dublin", "County Kildare"],
      url: canonical,
      provider: {
        "@type": "CleaningService",
        name: SITE.name,
        telephone: SITE.phoneHref,
        email: SITE.email,
        url: SITE.url,
      },
    },
  });

  if (!page) return null;
  const pricing = servicePricing(page);
  const faqs = page.faqs || DEFAULT_SERVICE_FAQS;
  const related = (page.related || []).map((s) => ({ slug: s, ...SERVICE_PAGES[s] })).filter((r) => r.name);

  return (
    <div className="App bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-white pt-32 pb-14 sm:pt-36" data-testid="service-hero">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <Reveal>
              <nav className="mb-4 flex items-center gap-1.5 text-sm text-slate-400" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-emerald-700">Home</Link>
                <span>/</span>
                <Link to="/services" className="hover:text-emerald-700">Services</Link>
                <span>/</span>
                <span className="text-slate-600">{page.name}</span>
              </nav>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
                <page.icon size={14} /> {page.category === "commercial" ? "Commercial" : "Residential"} • Dublin & Kildare
              </span>
              <h1 className="mt-5 font-display text-5xl font-black leading-[1.03] tracking-tighter text-slate-900 sm:text-6xl">
                {page.h1}
              </h1>
              <p className="mt-5 max-w-xl text-lg text-slate-600">{page.intro}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="#quote" data-testid="service-quote-btn" className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-7 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-800">
                  {page.businessCta ? "Request a Business Quote" : "Get a Free Quote"}
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </a>
                <a href={telLink()} onClick={track.phoneClick} data-testid="service-call-btn" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-4 text-base font-bold text-slate-800 transition-all hover:border-emerald-600 hover:text-emerald-700">
                  <Phone size={18} /> Call {SITE.phoneDisplay}
                </a>
                <a href={whatsappLink()} onClick={() => track.whatsappClick("service")} target="_blank" rel="noreferrer" data-testid="service-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 text-base font-bold text-white transition-transform hover:scale-105">
                  <MessageCircle size={18} /> WhatsApp
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(4,120,87,0.15)] max-h-[70vh]">
                <img src={IMG_FOR[page.category]} alt={`${page.h1} — professional cleaning in Dublin`} className="aspect-[4/5] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>

        <Marquee />

        {/* Detail sections */}
        <section className="bg-white py-16 sm:py-24" data-testid="service-detail">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {page.sections.map((sec, i) => (
                <Reveal key={sec.h2} delay={i * 0.06}>
                  <div className="h-full rounded-3xl border border-slate-200/70 bg-slate-50/60 p-7">
                    <h2 className="font-display text-xl font-bold text-slate-900">{sec.h2}</h2>
                    <ul className="mt-4 space-y-2.5">
                      {sec.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-slate-600">
                          <Check size={17} className="mt-0.5 shrink-0 text-emerald-600" /> {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
            {page.note && (
              <Reveal>
                <p className="mt-8 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <Info size={18} className="mt-0.5 shrink-0" /> {page.note}
                </p>
              </Reveal>
            )}
          </div>
        </section>

        {/* Pricing */}
        {pricing && (
          <section className="bg-slate-50 py-16 sm:py-24" data-testid="service-pricing">
            <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
              <Reveal>
                <h2 className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Prices starting from</h2>
                <p className="mt-3 text-sm text-slate-500">All prices are starting prices. The final quote depends on the size, condition and requirements of the property — send photos or a short video before booking.</p>
              </Reveal>
              <div className={`mt-8 grid gap-6 ${pricing.columns.length > 1 ? "sm:grid-cols-2" : ""}`}>
                {pricing.columns.map((col) => (
                  <Reveal key={col.title}>
                    <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                      <h3 className="font-display text-xl font-bold text-slate-900">{col.title}</h3>
                      <ul className="mt-5 space-y-3">
                        {col.rows.map((r) => (
                          <li key={r.label} className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3 text-sm">
                            <span className="text-slate-600">{r.label}</span>
                            <span className="shrink-0 font-bold text-emerald-700">{r.price.startsWith("€") ? `From ${r.price}` : r.price}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Business CTA banner for commercial pages */}
        {page.businessCta && (
          <section className="bg-white py-10">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col items-start justify-between gap-4 rounded-3xl bg-slate-950 p-8 text-white sm:flex-row sm:items-center">
                <div>
                  <h2 className="font-display text-2xl font-bold">Need recurring cleaning for your business?</h2>
                  <p className="mt-1 text-slate-300">Set up a weekly or customised cleaning contract with clear billing.</p>
                </div>
                <Link to="/business-cleaning-contracts-dublin" data-testid="service-contract-link" className="shrink-0 rounded-full bg-emerald-600 px-6 py-3.5 font-bold text-white transition-colors hover:bg-emerald-500">
                  Business Cleaning Contracts
                </Link>
              </div>
            </div>
          </section>
        )}

        <QuoteForm sourcePage={`/${slug}`} prefillServices={page.prefillServices || []} prefillCustomerType={page.prefillCustomerType || ""} />

        {/* FAQ */}
        <section className="bg-white py-16 sm:py-24" data-testid="service-faq">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="text-center font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Frequently Asked Questions</h2>
            </Reveal>
            <Accordion type="single" collapsible className="mt-8 space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} data-testid={`service-faq-${i}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 px-6">
                  <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="pb-5 text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Related services */}
        {related.length > 0 && (
          <section className="bg-slate-50 py-16 sm:py-24" data-testid="related-services">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Reveal>
                <h2 className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Related services</h2>
              </Reveal>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <Reveal key={r.slug}>
                    <Link to={`/${r.slug}`} data-testid={`related-${r.slug}`} className="group flex h-full items-start gap-4 rounded-3xl border border-slate-200/70 bg-white p-6 transition-all hover:-translate-y-1 hover:border-emerald-200 hover:shadow-lg">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 group-hover:bg-emerald-700 group-hover:text-white"><r.icon size={20} /></span>
                      <span>
                        <span className="flex items-center gap-1 font-display text-lg font-bold text-slate-900">{r.name} <ArrowUpRight size={15} className="text-emerald-500 opacity-0 transition-opacity group-hover:opacity-100" /></span>
                        <span className="mt-1 block text-sm text-slate-500">{r.metaDescription?.split(".")[0]}.</span>
                      </span>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
